/**
 * PermissionMatrixGrid (S2.2) — the rbacV2 grouped CRUD role-permission matrix.
 *
 * Renders when `governance.rbacV2` is ON (RolesTab keeps the legacy flat grid
 * verbatim for the OFF path). Rows are permission codes GROUPED by module
 * (collapsible header rows); every row carries its CRUD badge (C/R/U/D/M/B/S
 * from `PERMISSION_TYPE_CONFIG`) with the full-word i18n name as tooltip AND
 * `aria-label` — never a bare glyph. Columns are roles.
 *
 * THE WIRED SEAM (plan §0 thesis): fleet's `PermissionMatrix.tsx` shipped an
 * inherited-cell UI that was DEAD code — its `inheritedPermissions` prop
 * defaulted to `[]` with zero call-sites, so `inheritedPermissionSet` never
 * filled. Here the same visual seam is LIVE: every cell is classified from
 * `resolveEffectiveAccess().sources` over the DRAFT catalogs, so editing a
 * junior role's grant (or an `inheritsFrom` edge) lights the senior's inherited
 * cell immediately, before save. `buildPermissionMatrixModel` is the pure,
 * node-testable selector behind it (the S2.2 vitest asserts the inherited set
 * is non-empty over the seed — the dead code is provably reachable now).
 *
 * TWO visually distinct disabled-cell signals, each its OWN glyph + tooltip —
 * NEVER grey-only (grey/colour alone is not a signal):
 *   (a) inherited-junior / via-implication — <BranchesOutlined/> +
 *       "inherited from {role}" / "via implication of {permission}";
 *   (b) system-admin-locked — <LockOutlined/> + "locked: system administrator".
 *
 * ux-review note — SANCTIONED hide-don't-disable EXCEPTION: the disabled
 * checked checkboxes behind signals (a)/(b) are deliberately VISIBLE-but-
 * disabled. Hiding them would hide that the grant is effectively held; the
 * whole point of the seam is to EXPLAIN why a grant is present (provenance).
 * ux-review must not flag these cells as hide-don't-disable violations
 * (plan §Slice 2.2, ux-lens adjustment #4).
 *
 * Only a role's own DIRECT grants are editable; inherited / implied / locked
 * cells are read-only. The matrix scrolls in its OWN overflow-x container
 * (`Table` scroll.x inside `TableContainer`) — the page body never scrolls
 * horizontally.
 */

import { useMemo, useState } from 'react';
import { Button, Checkbox, Select, Table, Tag, Tooltip, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { BranchesOutlined, DownOutlined, LockOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import {
  ALL_PERMISSIONS,
  EXTENDED_PERMISSIONS,
  PERMISSION_CATALOG,
  PERMISSION_TYPE_CONFIG,
  getPermissionType,
  type PermissionType,
} from '@/constants/routePermissions';
import {
  resolveEffectiveAccess,
  resolveRoleClosure,
  type EffectiveAccess,
} from '@/lib/rbac';
import type { GovernanceRole } from './model';

const { Text } = Typography;

/** The status literal that keeps a role active in resolution / candidacy. */
const ACTIVE_STATUS = 'active';

/**
 * Flat i18n label key for a permission code (`governance.permissions.*`) —
 * deterministic camel-join over `.`/`_` segments, so implication-produced
 * phantom codes label correctly without a hand-kept map:
 *   `sales_orders.view` → `salesOrdersView` · `finance.costBasis.edit` →
 *   `financeCostBasisEdit` · `crossFranchiseView` → `crossFranchiseView`.
 * Byte-identical to the retired hand map for all 15 catalog codes.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function permissionLabelKey(code: string): string {
  const [head, ...rest] = code.split(/[._]/);
  return (
    (head ?? '') +
    rest.map((seg) => (seg ? seg[0].toUpperCase() + seg.slice(1) : seg)).join('')
  );
}

/**
 * The minimal role shape the pure matrix model reads — `GovernanceRole` is
 * assignable verbatim; tests can feed plain fixtures.
 */
export interface MatrixRoleInput {
  id: string;
  name: string;
  isSystem: boolean;
  status?: string;
  permissions: readonly string[];
  /** Mutable (not `readonly`) to stay assignable to `RoleClosureNode`. */
  inheritsFrom?: string[];
}

/**
 * Per-cell classification — the render contract:
 *   `direct`    editable checked checkbox (the ONLY editable granted state)
 *   `inherited` signal (a): checked + disabled + branches glyph + provenance
 *   `locked`    signal (b): checked + disabled + lock glyph (system admin)
 *   `absent`    editable unchecked checkbox
 */
export type MatrixCellSignal =
  | { kind: 'direct' }
  | { kind: 'inherited'; viaRoleIds: readonly string[]; viaPermissions: readonly string[] }
  | { kind: 'locked' }
  | { kind: 'absent' };

export interface MatrixPermissionRowModel {
  code: string;
  moduleKey: string;
  type: PermissionType;
  /**
   * True when the row is not statically catalogued — it materialized
   * ("auto-shows", plan §Slice 2.2 acceptance) because some active NON-system
   * role's effective set now includes it (e.g. granting `products.manage`
   * materializes the `products.view` row).
   */
  phantom: boolean;
}

export interface PermissionMatrixModel {
  modules: readonly { moduleKey: string; permissions: readonly MatrixPermissionRowModel[] }[];
  /** roleId → permission code → cell signal (covers every visible row × role). */
  cells: ReadonlyMap<string, ReadonlyMap<string, MatrixCellSignal>>;
  /**
   * Count of cells carrying the inherited signal — the "fleet dead-code is now
   * reachable" probe. Non-empty over the seed (asserted by the S2.2 vitest).
   */
  inheritedCellCount: number;
}

/** Module of a code by its first dotted segment (`products.*` → masterData…). */
const MODULE_OF_PREFIX: ReadonlyMap<string, string> = new Map(
  PERMISSION_CATALOG.flatMap((module) =>
    module.permissions.map((code) => [code.split('.')[0], module.moduleKey] as const)
  )
);

function moduleOfCode(code: string): string {
  return MODULE_OF_PREFIX.get(code.split('.')[0]) ?? 'system';
}

/**
 * Overlay the RolesTab edit buffers onto the server roles — the LIVE draft
 * catalog every per-role probe resolves over, so a junior-grant or edge edit
 * lights the senior's inherited cell before save. Missing draft entries fall
 * back to the server values (pre-load / untouched roles).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function overlayDraft(
  roles: readonly GovernanceRole[],
  draftPermissions: Readonly<Record<string, ReadonlySet<string>>>,
  draftInherits: Readonly<Record<string, readonly string[]>>
): MatrixRoleInput[] {
  return roles.map((role) => {
    const inherits = draftInherits[role.id] ?? role.inheritsFrom;
    return {
      ...role,
      permissions: draftPermissions[role.id] ? [...draftPermissions[role.id]] : role.permissions,
      inheritsFrom: inherits ? [...inherits] : undefined,
    };
  });
}

/**
 * Roles selectable as `inheritsFrom` parents for `roleId`. Excludes itself,
 * system roles (admin's ALL-grants make inheriting it meaningless), inactive
 * roles, and any candidate whose OWN closure already contains `roleId` — that
 * edge would close a cycle (the resolver rejects cycles; S2.3 owns the richer
 * validation UX). Options are FILTERED here, not disabled (hide-don't-disable).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function inheritCandidateIds(
  roleId: string,
  roles: readonly MatrixRoleInput[]
): string[] {
  return roles
    .filter((candidate) => candidate.id !== roleId && !candidate.isSystem)
    .filter((candidate) => !candidate.status || candidate.status === ACTIVE_STATUS)
    .filter((candidate) => !resolveRoleClosure([candidate.id], roles).has(roleId))
    .map((candidate) => candidate.id);
}

function classifySignal(
  role: MatrixRoleInput,
  code: string,
  directSet: ReadonlySet<string>,
  access: EffectiveAccess
): MatrixCellSignal {
  // Signal (b): the isSystem admin column is locked wholesale — its power is the
  // seeded ALL-grants (resolver invariant), never a bypass; the matrix must not
  // let it be narrowed. Rendered checked + lock glyph + own tooltip.
  if (role.isSystem) return { kind: 'locked' };
  if (directSet.has(code)) return { kind: 'direct' };
  const sources = access.sources.get(code) ?? [];
  const viaRoleIds = [
    ...new Set(
      sources.flatMap((s) => (s.kind === 'inherited-junior' ? [s.roleId] : []))
    ),
  ];
  const viaPermissions = [
    ...new Set(sources.flatMap((s) => (s.kind === 'implication' ? [s.ofPermission] : []))),
  ];
  if (viaRoleIds.length > 0 || viaPermissions.length > 0) {
    return { kind: 'inherited', viaRoleIds, viaPermissions };
  }
  return { kind: 'absent' };
}

/**
 * The pure matrix selector — every cell classified from
 * `resolveEffectiveAccess().sources` with each role probed AS a principal
 * holding just itself (Axis A only; `orgs` empty — WHERE never leaks into the
 * WHAT matrix). Zero React — node-testable, the same discipline as the
 * resolver it consumes.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function buildPermissionMatrixModel(
  roles: readonly MatrixRoleInput[]
): PermissionMatrixModel {
  const accessByRole = new Map<string, EffectiveAccess>();
  for (const role of roles) {
    accessByRole.set(
      role.id,
      resolveEffectiveAccess({
        identity: { userId: `matrix-probe-${role.id}`, roleIds: [role.id], assignments: [] },
        roles,
        orgs: [],
      })
    );
  }

  // Phantom rows: codes beyond the static catalog materialized by any ACTIVE
  // NON-system role's effective set. System roles are excluded as drivers —
  // admin derives every view by design and would pin every phantom permanently,
  // which would defeat the "auto-shows on grant" behaviour.
  const staticCodes = new Set<string>([...ALL_PERMISSIONS, ...EXTENDED_PERMISSIONS]);
  const phantomCodes = new Set<string>();
  for (const role of roles) {
    if (role.isSystem) continue;
    if (role.status && role.status !== ACTIVE_STATUS) continue;
    for (const code of accessByRole.get(role.id)?.grants ?? []) {
      if (!staticCodes.has(code)) phantomCodes.add(code);
    }
  }

  const modules = PERMISSION_CATALOG.map((module) => ({
    moduleKey: module.moduleKey,
    permissions: module.permissions.map((code) => ({
      code,
      moduleKey: module.moduleKey,
      type: getPermissionType(code),
      phantom: false,
    })),
  }));
  const byModuleKey = new Map(modules.map((m) => [m.moduleKey, m]));
  for (const code of EXTENDED_PERMISSIONS) {
    byModuleKey.get(moduleOfCode(code))?.permissions.push({
      code,
      moduleKey: moduleOfCode(code),
      type: getPermissionType(code),
      phantom: false,
    });
  }
  for (const code of [...phantomCodes].sort()) {
    byModuleKey.get(moduleOfCode(code))?.permissions.push({
      code,
      moduleKey: moduleOfCode(code),
      type: getPermissionType(code),
      phantom: true,
    });
  }

  const rowCodes = modules.flatMap((m) => m.permissions.map((p) => p.code));
  const cells = new Map<string, Map<string, MatrixCellSignal>>();
  let inheritedCellCount = 0;
  for (const role of roles) {
    const access = accessByRole.get(role.id);
    if (!access) continue;
    const directSet = new Set(role.permissions);
    const roleCells = new Map<string, MatrixCellSignal>();
    for (const code of rowCodes) {
      const signal = classifySignal(role, code, directSet, access);
      if (signal.kind === 'inherited') inheritedCellCount += 1;
      roleCells.set(code, signal);
    }
    cells.set(role.id, roleCells);
  }

  return { modules, cells, inheritedCellCount };
}

// --- Component ---------------------------------------------------------------

type MatrixTableRow =
  | { rowType: 'inherits'; key: string }
  | { rowType: 'module'; key: string; moduleKey: string; total: number }
  | {
      rowType: 'permission';
      key: string;
      code: string;
      moduleKey: string;
      type: PermissionType;
      phantom: boolean;
    };

export interface PermissionMatrixGridProps {
  roles: readonly GovernanceRole[];
  loading: boolean;
  editMode: boolean;
  /** roleId → draft permission set (the RolesTab edit buffer). */
  draftPermissions: Readonly<Record<string, ReadonlySet<string>>>;
  /** roleId → draft `inheritsFrom` (the RolesTab edge edit buffer). */
  draftInherits: Readonly<Record<string, readonly string[]>>;
  onTogglePermission: (roleId: string, code: string, checked: boolean) => void;
  onChangeInherits: (roleId: string, inheritsFrom: string[]) => void;
}

const LABEL_COL_WIDTH = 280;
const ROLE_COL_WIDTH = 200;

const PermissionMatrixGrid = ({
  roles,
  loading,
  editMode,
  draftPermissions,
  draftInherits,
  onTogglePermission,
  onChangeInherits,
}: PermissionMatrixGridProps) => {
  const { t } = useTranslation('governance');
  const { token } = theme.useToken();
  const [collapsedModules, setCollapsedModules] = useState<ReadonlySet<string>>(new Set());

  const overlaid = useMemo(
    () => overlayDraft(roles, draftPermissions, draftInherits),
    [roles, draftPermissions, draftInherits]
  );
  const model = useMemo(() => buildPermissionMatrixModel(overlaid), [overlaid]);
  const roleNameById = useMemo(
    () => new Map(roles.map((role) => [role.id, role.name])),
    [roles]
  );

  const rows = useMemo<MatrixTableRow[]>(() => {
    const out: MatrixTableRow[] = [{ rowType: 'inherits', key: '__inherits__' }];
    for (const module of model.modules) {
      out.push({
        rowType: 'module',
        key: `module-${module.moduleKey}`,
        moduleKey: module.moduleKey,
        total: module.permissions.length,
      });
      if (collapsedModules.has(module.moduleKey)) continue;
      for (const permission of module.permissions) {
        out.push({ rowType: 'permission', key: permission.code, ...permission });
      }
    }
    return out;
  }, [model, collapsedModules]);

  const toggleModule = (moduleKey: string) => {
    setCollapsedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleKey)) next.delete(moduleKey);
      else next.add(moduleKey);
      return next;
    });
  };

  const permissionLabel = (code: string) => t(`permissions.${permissionLabelKey(code)}`);

  const renderTypeBadge = (type: PermissionType) => {
    const config = PERMISSION_TYPE_CONFIG[type];
    // Full-word accessible name: tooltip + aria-label; the glyph/colour are
    // secondary cues only (S0.2 rule — never a bare glyph). Focusable so the
    // tooltip reveals on keyboard focus too (never hover-only).
    const word = t(config.i18nKey);
    return (
      <Tooltip title={word} trigger={['hover', 'focus']}>
        <Tag
          tabIndex={0}
          role="img"
          aria-label={word}
          style={{
            color: token[config.colorToken],
            borderColor: token[config.colorToken],
            background: 'transparent',
            marginInlineEnd: 0,
          }}
        >
          {config.glyph}
        </Tag>
      </Tooltip>
    );
  };

  const renderLabelCell = (row: MatrixTableRow) => {
    if (row.rowType === 'inherits') {
      return <Text strong>{t('matrix.inheritsFromLabel')}</Text>;
    }
    if (row.rowType === 'module') {
      const moduleLabel = t(`permissionModules.${row.moduleKey}`);
      const isCollapsed = collapsedModules.has(row.moduleKey);
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: token.paddingXS }}>
          <Button
            type="text"
            size="small"
            icon={isCollapsed ? <RightOutlined /> : <DownOutlined />}
            aria-label={t(isCollapsed ? 'matrix.expandModule' : 'matrix.collapseModule', {
              module: moduleLabel,
            })}
            onClick={() => toggleModule(row.moduleKey)}
          />
          <Text strong>{moduleLabel}</Text>
          <Text type="secondary">{t('matrix.permissionTotal', { count: row.total })}</Text>
        </span>
      );
    }
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: token.paddingXS }}>
        {renderTypeBadge(row.type)}
        <span>{permissionLabel(row.code)}</span>
      </span>
    );
  };

  const renderInheritsCell = (role: GovernanceRole) => {
    // Admin (isSystem) holds ALL grants by seed invariant — an inherits edit
    // would be meaningless, so the cell renders the '-' empty value.
    if (role.isSystem) return renderDash('');
    const current = draftInherits[role.id] ?? role.inheritsFrom ?? [];
    if (!editMode) {
      return current.length > 0
        ? current.map((id) => <Tag key={id}>{roleNameById.get(id) ?? id}</Tag>)
        : renderDash('');
    }
    const options = inheritCandidateIds(role.id, overlaid).map((id) => ({
      value: id,
      label: roleNameById.get(id) ?? id,
    }));
    return (
      <Select
        mode="multiple"
        size="large"
        style={{ width: '100%' }}
        value={[...current]}
        options={options}
        placeholder={t('matrix.inheritsPlaceholder')}
        maxTagCount="responsive"
        onChange={(value) => onChangeInherits(role.id, value)}
      />
    );
  };

  const renderPermissionCell = (role: GovernanceRole, row: MatrixTableRow & { rowType: 'permission' }) => {
    const signal = model.cells.get(role.id)?.get(row.code) ?? { kind: 'absent' as const };

    if (signal.kind === 'locked') {
      // Signal (b) — system-admin-locked: distinct glyph + its own tooltip.
      // (Sanctioned hide-don't-disable exception — see file docblock.)
      const label = t('matrix.lockedSystemAdmin');
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: token.paddingXXS }}>
          <Checkbox checked disabled />
          <Tooltip title={label} trigger={['hover', 'focus']}>
            <LockOutlined
              tabIndex={0}
              aria-label={label}
              style={{ color: token.colorTextSecondary }}
            />
          </Tooltip>
        </span>
      );
    }

    if (signal.kind === 'inherited') {
      // Signal (a) — inherited-junior / via-implication: fleet's dead seam, now
      // fed live by resolveEffectiveAccess().sources. Checked + disabled +
      // branches glyph + per-source provenance tooltip — never grey-only.
      // (Sanctioned hide-don't-disable exception — see file docblock.)
      const lines = [
        ...signal.viaRoleIds.map((id) =>
          t('matrix.inheritedFrom', { role: roleNameById.get(id) ?? id })
        ),
        ...signal.viaPermissions.map((code) =>
          t('matrix.viaImplication', { permission: permissionLabel(code) })
        ),
      ];
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: token.paddingXXS }}>
          <Checkbox checked disabled />
          <Tooltip
            trigger={['hover', 'focus']}
            title={
              <>
                {lines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </>
            }
          >
            <BranchesOutlined
              tabIndex={0}
              aria-label={lines.join(' · ')}
              style={{ color: token.colorPrimary }}
            />
          </Tooltip>
        </span>
      );
    }

    // Direct grants are the ONLY editable granted state; absent cells toggle on.
    return (
      <Checkbox
        checked={signal.kind === 'direct'}
        disabled={!editMode}
        onChange={(e) => onTogglePermission(role.id, row.code, e.target.checked)}
      />
    );
  };

  const columns: ColumnsType<MatrixTableRow> = [
    {
      title: t('matrix.permission'),
      key: 'label',
      width: LABEL_COL_WIDTH,
      fixed: 'left',
      onCell: (row) => (row.rowType === 'module' ? { colSpan: 1 + roles.length } : {}),
      render: (_: unknown, row) => renderLabelCell(row),
    },
    ...roles.map((role) => ({
      title: <Text strong>{role.name}</Text>,
      key: role.id,
      align: 'center' as const,
      width: ROLE_COL_WIDTH,
      onCell: (row: MatrixTableRow) => (row.rowType === 'module' ? { colSpan: 0 } : {}),
      render: (_: unknown, row: MatrixTableRow) => {
        if (row.rowType === 'module') return null;
        if (row.rowType === 'inherits') return renderInheritsCell(role);
        return renderPermissionCell(role, row);
      },
    })),
  ];

  return (
    // The matrix's OWN overflow-x container: Table scroll.x scrolls inside
    // TableContainer; the page body never scrolls horizontally.
    <TableContainer>
      <Table<MatrixTableRow>
        rowKey="key"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: LABEL_COL_WIDTH + roles.length * ROLE_COL_WIDTH }}
        locale={{ emptyText: <ListEmptyState title={t('empty.roles')} /> }}
      />
    </TableContainer>
  );
};

export default PermissionMatrixGrid;
